import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Plus, ChevronLeft, ChevronRight, Search, Settings, Calendar as CalIcon,
  CheckSquare, X, Clock, Users, Video, MapPin, AlignLeft, Bell, Lock, Briefcase,
  Trash2, Edit3, ChevronDown, Palette, Building2, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { calendarApi, usersApi, tasksApi, customersApi } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { usePageHeader } from "../contexts/PageHeaderContext";
import TaskDetailModal from "../components/TaskDetailModal";
import "./calendar.css";

// ---------- date helpers -----------------------------------------------------
const MS_DAY = 24 * 60 * 60 * 1000;
const startOfDay = (d) => { const x = new Date(d); x.setHours(0,0,0,0); return x; };
const startOfWeek = (d) => { const x = startOfDay(d); const dow = x.getDay(); x.setDate(x.getDate() - dow); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const isSameDay = (a, b) => a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth() && a.getDate()===b.getDate();
const toISO = (d) => new Date(d).toISOString();
// yyyy-mm-ddThh:mm (local) — for datetime-local inputs
const toLocalDatetime = (d) => {
  const x = new Date(d);
  const p = (n) => String(n).padStart(2, "0");
  return `${x.getFullYear()}-${p(x.getMonth()+1)}-${p(x.getDate())}T${p(x.getHours())}:${p(x.getMinutes())}`;
};
const fromLocalDatetime = (s) => new Date(s);
const fmtHour = (h) => `${((h+11)%12)+1} ${h<12?"AM":"PM"}`;
const fmtDayShort = (d) => d.toLocaleDateString(undefined, { weekday: "short" }).toUpperCase();
const fmtMonthYear = (d) => d.toLocaleDateString(undefined, { month: "long", year: "numeric" });

const HOUR_HEIGHT = 48; // px per hour in week/day view
const NOTIFY_PRESETS = [
  { minutes: 0, label: "When event starts" },
  { minutes: 15, label: "15 minutes before" },
  { minutes: 30, label: "30 minutes before" },
  { minutes: 60, label: "1 hour before" },
  { minutes: 120, label: "2 hours before" },
];

// ---------- Root page --------------------------------------------------------
export default function Calendar() {
  const { user } = useAuth();
  const [view, setView] = useState("week"); // 'week' | 'day' | 'month'
  const [anchorDate, setAnchorDate] = useState(startOfDay(new Date()));
  const [calendars, setCalendars] = useState([]);
  const [checked, setChecked] = useState({}); // { calendarId: true/false }
  const [events, setEvents] = useState([]);
  const [staff, setStaff] = useState([]); // full user list (for guests + share pickers)
  const [customers, setCustomers] = useState([]);
  const [openEventModal, setOpenEventModal] = useState(false);
  const [modalPrefill, setModalPrefill] = useState(null); // {start,end} or existing event
  const [taskOpen, setTaskOpen] = useState(null); // task doc when opening a task from cal
  const [newTaskOpen, setNewTaskOpen] = useState(false);
  const [createMenu, setCreateMenu] = useState(false);
  const [calendarSettings, setCalendarSettings] = useState(null); // calendar row when editing
  const [companyAdmin, setCompanyAdmin] = useState(false);

  useEffect(() => {
    setCompanyAdmin(user?.role === "admin" || user?.role === "super_admin");
  }, [user]);

  // ---------- data fetch -----------------------------------------------------
  const fetchCalendars = useCallback(async () => {
    try {
      const list = await calendarApi.listCalendars();
      setCalendars(list);
      setChecked((prev) => {
        const next = { ...prev };
        list.forEach((c) => { if (next[c.id] === undefined) next[c.id] = true; });
        // Prune ids no longer present
        Object.keys(next).forEach((k) => { if (!list.some((c) => c.id === k)) delete next[k]; });
        return next;
      });
    } catch (err) {
      console.error(err);
      toast.error("Could not load calendars");
    }
  }, []);

  const fetchEvents = useCallback(async () => {
    const range = viewRange(view, anchorDate);
    const ids = Object.entries(checked).filter(([, v]) => v).map(([k]) => k).join(",");
    if (!ids) { setEvents([]); return; }
    try {
      const rows = await calendarApi.listEvents({ start: toISO(range.start), end: toISO(range.end), calendarIds: ids });
      setEvents(rows);
    } catch (err) {
      console.error(err);
    }
  }, [view, anchorDate, checked]);

  useEffect(() => { fetchCalendars(); }, [fetchCalendars]);
  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  // Preload team + customer light lists (used by event modal guest picker)
  useEffect(() => {
    (async () => {
      try {
        const [u, c] = await Promise.all([
          usersApi.list().catch(() => []),
          customersApi.list().catch(() => []),
        ]);
        setStaff(u || []);
        setCustomers(c || []);
      } catch { /* non-fatal */ }
    })();
  }, []);

  // ---------- navigation ----------------------------------------------------
  const goToday = () => setAnchorDate(startOfDay(new Date()));
  const goPrev = () => setAnchorDate(addDays(anchorDate, view === "week" ? -7 : view === "day" ? -1 : -30));
  const goNext = () => setAnchorDate(addDays(anchorDate, view === "week" ? 7 : view === "day" ? 1 : 30));

  // ---------- open task from a virtual event -------------------------------
  const openTaskById = async (taskId) => {
    try {
      const list = await tasksApi.list({});
      const t = (list || []).find((x) => x.id === taskId);
      if (t) setTaskOpen(t);
      else toast.error("Task not found");
    } catch { toast.error("Failed to open task"); }
  };

  const canManageEvent = (ev) => {
    if (ev.source === "task") return false;
    if (companyAdmin) return true;
    return ev.ownerUserId === user?.id;
  };

  // ---------- render --------------------------------------------------------
  const headerLabel = useMemo(() => {
    if (view === "week") {
      const s = startOfWeek(anchorDate);
      const e = addDays(s, 6);
      const sameMonth = s.getMonth() === e.getMonth();
      if (sameMonth) return `${s.toLocaleDateString(undefined,{month:"long",year:"numeric"})}`;
      return `${s.toLocaleDateString(undefined,{month:"short"})} – ${e.toLocaleDateString(undefined,{month:"short",year:"numeric"})}`;
    }
    if (view === "day") return anchorDate.toLocaleDateString(undefined,{weekday:"long",month:"long",day:"numeric",year:"numeric"});
    return fmtMonthYear(anchorDate);
  }, [view, anchorDate]);

  usePageHeader({ title: "Calendar" });

  return (
    <div className="flex-1 min-h-0 flex cal-page" data-testid="calendar-page">
      {/* ---------- Left rail ---------- */}
      <aside className="hidden md:flex md:flex-col w-[260px] shrink-0 border-r" style={{ backgroundColor: "#f8fafc", borderColor: "#e2e8f0" }}>
        <div className="p-3 border-b" style={{ borderColor: "#e2e8f0" }}>
          <CreateMenu
            open={createMenu} setOpen={setCreateMenu}
            onCreateEvent={() => { setModalPrefill(null); setOpenEventModal(true); setCreateMenu(false); }}
            onCreateTask={() => { setNewTaskOpen(true); setCreateMenu(false); }}
          />
        </div>
        <div className="px-3 py-3 border-b" style={{ borderColor: "#e2e8f0" }}>
          <MiniMonth anchor={anchorDate} onPick={(d) => setAnchorDate(d)} />
        </div>
        <div className="flex-1 overflow-y-auto py-2">
          <SectionHeader title="My calendars" icon={CalIcon} />
          {calendars.filter((c) => c.ownerUserId === user?.id).map((c) => (
            <CalendarRow key={c.id} cal={c} checked={!!checked[c.id]} onToggle={() => setChecked((p) => ({ ...p, [c.id]: !p[c.id] }))} onEdit={() => setCalendarSettings(c)} canEdit />
          ))}
          <SectionHeader title="Shared with me" icon={Users} />
          {calendars.filter((c) => c.isCompany || (c.ownerUserId !== user?.id && (c.sharedWith || []).includes(user?.id))).map((c) => (
            <CalendarRow key={c.id} cal={c} checked={!!checked[c.id]} onToggle={() => setChecked((p) => ({ ...p, [c.id]: !p[c.id] }))} onEdit={companyAdmin && c.isCompany ? () => setCalendarSettings(c) : undefined} canEdit={companyAdmin && c.isCompany} />
          ))}
          {companyAdmin && (
            <>
              <SectionHeader title="All staff" icon={Users} />
              {calendars
                .filter((c) => c.isPersonal && c.ownerUserId !== user?.id && !(c.sharedWith || []).includes(user?.id))
                .map((c) => (
                  <CalendarRow key={c.id} cal={c} checked={!!checked[c.id]} onToggle={() => setChecked((p) => ({ ...p, [c.id]: !p[c.id] }))} onEdit={() => setCalendarSettings(c)} canEdit />
                ))}
            </>
          )}
        </div>
      </aside>

      {/* ---------- Main pane ---------- */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Toolbar */}
        <div className="flex items-center gap-3 px-4 py-3 border-b" style={{ borderColor: "#e2e8f0", backgroundColor: "#ffffff" }}>
          <button data-testid="cal-today" onClick={goToday} className="px-3 h-8 rounded-full border text-[13px] font-medium hover:bg-slate-50" style={{ borderColor: "#cbd5e1", color: "#0f172a", backgroundColor: "#ffffff" }}>Today</button>
          <button data-testid="cal-prev" onClick={goPrev} className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-slate-100" style={{ color: "#475569" }}><ChevronLeft className="h-4 w-4" color="#475569" /></button>
          <button data-testid="cal-next" onClick={goNext} className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-slate-100" style={{ color: "#475569" }}><ChevronRight className="h-4 w-4" color="#475569" /></button>
          <div className="text-[19px] font-semibold" style={{ color: "#0f172a" }} data-testid="cal-header-label">{headerLabel}</div>
          <div className="flex-1" />
          <div className="inline-flex rounded-full border p-0.5" style={{ borderColor: "#cbd5e1", backgroundColor: "#ffffff" }} data-testid="cal-view-switch">
            {[["day", "Day"], ["week", "Week"], ["month", "Month"]].map(([k, label]) => (
              <button key={k} onClick={() => setView(k)} data-testid={`cal-view-${k}`}
                className="px-3 h-7 rounded-full text-[12.5px] font-medium"
                style={{
                  backgroundColor: view === k ? "#dbeafe" : "transparent",
                  color: view === k ? "#1d4ed8" : "#334155",
                }}>{label}</button>
            ))}
          </div>
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-auto" style={{ backgroundColor: "#ffffff" }}>
          {view === "week" && <WeekView anchor={anchorDate} events={events} onNewEvent={(range) => { setModalPrefill(range); setOpenEventModal(true); }} onOpenEvent={(ev) => { if (ev.source === "task") openTaskById(ev.taskId); else setModalPrefill(ev), setOpenEventModal(true); }} />}
          {view === "day" && <DayView anchor={anchorDate} events={events} onNewEvent={(range) => { setModalPrefill(range); setOpenEventModal(true); }} onOpenEvent={(ev) => { if (ev.source === "task") openTaskById(ev.taskId); else setModalPrefill(ev), setOpenEventModal(true); }} />}
          {view === "month" && <MonthView anchor={anchorDate} events={events} onOpenEvent={(ev) => { if (ev.source === "task") openTaskById(ev.taskId); else setModalPrefill(ev), setOpenEventModal(true); }} onDayClick={(d) => { setAnchorDate(d); setView("day"); }} />}
        </div>
      </div>

      {/* ---------- Modals ---------- */}
      {openEventModal && (
        <EventModal
          initial={modalPrefill}
          onClose={() => { setOpenEventModal(false); setModalPrefill(null); }}
          onSaved={() => { setOpenEventModal(false); setModalPrefill(null); fetchEvents(); }}
          onDeleted={() => { setOpenEventModal(false); setModalPrefill(null); fetchEvents(); }}
          calendars={calendars.filter((c) => c.ownerUserId === user?.id || (companyAdmin && (c.isCompany || c.isPersonal)))}
          staff={staff}
          customers={customers}
          canManage={modalPrefill?.id ? canManageEvent(modalPrefill) : true}
        />
      )}
      {taskOpen && (
        <TaskDetailModal
          task={taskOpen} users={staff} canManage={companyAdmin} canNote={true}
          onClose={() => setTaskOpen(null)}
          onUpdated={() => fetchEvents()}
        />
      )}
      {calendarSettings && (
        <CalendarSettingsModal
          cal={calendarSettings} staff={staff}
          canDelete={!calendarSettings.isPersonal && !calendarSettings.isCompany && companyAdmin}
          onClose={() => setCalendarSettings(null)}
          onSaved={() => { setCalendarSettings(null); fetchCalendars(); }}
          onDeleted={() => { setCalendarSettings(null); fetchCalendars(); }}
        />
      )}
      {newTaskOpen && (
        <NewTaskModal
          staff={staff} customers={customers}
          onClose={() => setNewTaskOpen(false)}
          onCreated={() => { setNewTaskOpen(false); fetchEvents(); }}
        />
      )}
    </div>
  );
}

// ---------- helpers ----------
function viewRange(view, anchor) {
  if (view === "week") {
    const s = startOfWeek(anchor);
    return { start: s, end: addDays(s, 7) };
  }
  if (view === "day") {
    const s = startOfDay(anchor);
    return { start: s, end: addDays(s, 1) };
  }
  // month
  const s = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const e = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1);
  return { start: s, end: e };
}

// ---------- CreateMenu ----------
function CreateMenu({ open, setOpen, onCreateEvent, onCreateTask }) {
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, [setOpen]);
  return (
    <div className="relative" ref={ref}>
      <button data-testid="cal-create-btn" onClick={() => setOpen((v) => !v)}
        className="w-full inline-flex items-center gap-2 px-4 py-2.5 rounded-full shadow-md hover:shadow-lg transition-shadow"
        style={{ backgroundColor: "#2563eb", color: "#ffffff" }}>
        <Plus className="h-4 w-4" color="#ffffff" />
        <span style={{ color: "#ffffff" }} className="text-[14px] font-semibold">Create</span>
        <ChevronDown className="h-4 w-4 ml-auto" color="#ffffff" />
      </button>
      {open && (
        <div className="absolute z-30 left-0 right-0 mt-1 rounded-md border shadow-lg overflow-hidden" style={{ backgroundColor: "#ffffff", borderColor: "#e2e8f0" }} data-testid="cal-create-menu">
          <MenuItem icon={CalIcon} label="Event" onClick={onCreateEvent} testid="cal-create-event" />
          <MenuItem icon={CheckSquare} label="Task" onClick={onCreateTask} testid="cal-create-task" />
        </div>
      )}
    </div>
  );
}

function MenuItem({ icon: Icon, label, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-slate-50"
      style={{ color: "#0f172a", backgroundColor: "transparent" }}>
      <Icon className="h-4 w-4" style={{ color: "#475569" }} color="#475569" />
      <span style={{ color: "#0f172a" }} className="text-[13px]">{label}</span>
    </button>
  );
}

// ---------- MiniMonth ----------
function MiniMonth({ anchor, onPick }) {
  const [cursor, setCursor] = useState(new Date(anchor.getFullYear(), anchor.getMonth(), 1));
  useEffect(() => { setCursor(new Date(anchor.getFullYear(), anchor.getMonth(), 1)); }, [anchor]);
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const gridStart = addDays(first, -first.getDay());
  const days = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  const today = new Date();
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[13px] font-semibold" style={{ color: "#0f172a" }}>{fmtMonthYear(cursor)}</div>
        <div className="flex items-center gap-1">
          <button onClick={() => setCursor(addDays(cursor, -28))} className="h-6 w-6 rounded flex items-center justify-center hover:bg-slate-200"><ChevronLeft className="h-3.5 w-3.5" color="#475569" /></button>
          <button onClick={() => setCursor(addDays(cursor, 32))} className="h-6 w-6 rounded flex items-center justify-center hover:bg-slate-200"><ChevronRight className="h-3.5 w-3.5" color="#475569" /></button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-y-0.5 mb-1 text-[10px] font-medium text-center" style={{ color: "#94a3b8" }}>
        {["S","M","T","W","T","F","S"].map((d,i) => <div key={i}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-y-0.5 text-center">
        {days.map((d, i) => {
          const inMonth = d.getMonth() === cursor.getMonth();
          const isToday = isSameDay(d, today);
          const isAnchor = isSameDay(d, anchor);
          return (
            <button key={i} onClick={() => onPick(startOfDay(d))}
              className="mx-auto h-6 w-6 rounded-full text-[11.5px] flex items-center justify-center transition-colors"
              style={{
                backgroundColor: isAnchor ? "#2563eb" : (isToday ? "#dbeafe" : "transparent"),
                color: isAnchor ? "#ffffff" : (inMonth ? "#0f172a" : "#cbd5e1"),
                fontWeight: isAnchor || isToday ? 700 : 400,
              }}>
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------- SectionHeader ----------
function SectionHeader({ title, icon: Icon }) {
  return (
    <div className="px-3 pt-3 pb-1 flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-wider" style={{ color: "#64748b" }}>
      <Icon className="h-3 w-3" color="#64748b" /> {title}
    </div>
  );
}

// ---------- CalendarRow ----------
function CalendarRow({ cal, checked, onToggle, onEdit, canEdit }) {
  return (
    <div className="group flex items-center gap-2 px-3 py-1.5 hover:bg-slate-100" data-testid={`cal-row-${cal.id}`}>
      <button onClick={onToggle} className="h-4 w-4 rounded flex items-center justify-center transition-colors" style={{ backgroundColor: checked ? cal.color : "transparent", borderColor: cal.color, borderWidth: 1.5, borderStyle: "solid" }} title="Toggle visibility">
        {checked && <span style={{ color: "#ffffff", fontSize: "10px", fontWeight: 900, lineHeight: 1 }}>✓</span>}
      </button>
      <div className="flex-1 min-w-0 text-[12.5px] truncate flex items-center gap-1.5" style={{ color: "#0f172a" }}>
        {cal.isCompany && <Building2 className="h-3 w-3 shrink-0" color="#14b8a6" />}
        {cal.name}
      </div>
      {canEdit && onEdit && (
        <button onClick={onEdit} className="opacity-0 group-hover:opacity-100 h-6 w-6 rounded flex items-center justify-center hover:bg-slate-200" data-testid={`cal-edit-${cal.id}`} title="Edit calendar">
          <Settings className="h-3.5 w-3.5" color="#64748b" />
        </button>
      )}
    </div>
  );
}

// ---------- WeekView ----------
function WeekView({ anchor, events, onNewEvent, onOpenEvent }) {
  const days = Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(anchor), i));
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const today = new Date();

  const layoutForDay = (day) => {
    // Events overlapping this day (excluding all-day for the timed grid)
    return events
      .filter((ev) => !ev.allDay)
      .filter((ev) => {
        try {
          const s = new Date(ev.start); const e = new Date(ev.end);
          return s < addDays(day, 1) && e > day;
        } catch { return false; }
      });
  };

  return (
    <div className="min-w-[900px]">
      {/* Day header */}
      <div className="grid" style={{ gridTemplateColumns: "56px repeat(7, 1fr)", position: "sticky", top: 0, zIndex: 3, backgroundColor: "#ffffff", borderBottom: "1px solid #e2e8f0" }}>
        <div />
        {days.map((d) => {
          const isToday = isSameDay(d, today);
          return (
            <div key={d.toISOString()} className="py-2 text-center border-l" style={{ borderColor: "#e2e8f0" }}>
              <div className="text-[10.5px] font-medium" style={{ color: isToday ? "#1d4ed8" : "#64748b" }}>{fmtDayShort(d)}</div>
              <div className="mt-1 mx-auto h-8 w-8 rounded-full inline-flex items-center justify-center text-[16px] font-medium" style={{ backgroundColor: isToday ? "#2563eb" : "transparent", color: isToday ? "#ffffff" : "#0f172a" }}>{d.getDate()}</div>
            </div>
          );
        })}
      </div>
      {/* Hour grid */}
      <div className="relative grid" style={{ gridTemplateColumns: "56px repeat(7, 1fr)" }}>
        {/* Hour labels column */}
        <div>
          {hours.map((h) => (
            <div key={h} style={{ height: HOUR_HEIGHT, position: "relative" }}>
              <div className="absolute top-0 right-2 -mt-1.5 text-[10px]" style={{ color: "#94a3b8" }}>{h === 0 ? "" : fmtHour(h)}</div>
            </div>
          ))}
        </div>
        {days.map((d) => {
          const dayEvents = layoutForDay(d);
          return (
            <DayColumn key={d.toISOString()} day={d} events={dayEvents} onNewEvent={onNewEvent} onOpenEvent={onOpenEvent} />
          );
        })}
      </div>
    </div>
  );
}

// ---------- DayView ----------
function DayView({ anchor, events, onNewEvent, onOpenEvent }) {
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const dayEvents = events.filter((ev) => {
    try {
      const s = new Date(ev.start); const e = new Date(ev.end);
      return s < addDays(anchor, 1) && e > anchor && !ev.allDay;
    } catch { return false; }
  });
  return (
    <div className="min-w-[600px]">
      <div className="grid" style={{ gridTemplateColumns: "56px 1fr", position: "sticky", top: 0, zIndex: 3, backgroundColor: "#ffffff", borderBottom: "1px solid #e2e8f0" }}>
        <div />
        <div className="py-2 text-center border-l" style={{ borderColor: "#e2e8f0" }}>
          <div className="text-[10.5px] font-medium" style={{ color: "#64748b" }}>{fmtDayShort(anchor)}</div>
          <div className="mt-1 mx-auto h-8 w-8 rounded-full inline-flex items-center justify-center text-[16px] font-medium" style={{ backgroundColor: "#2563eb", color: "#ffffff" }}>{anchor.getDate()}</div>
        </div>
      </div>
      <div className="relative grid" style={{ gridTemplateColumns: "56px 1fr" }}>
        <div>
          {hours.map((h) => (
            <div key={h} style={{ height: HOUR_HEIGHT, position: "relative" }}>
              <div className="absolute top-0 right-2 -mt-1.5 text-[10px]" style={{ color: "#94a3b8" }}>{h === 0 ? "" : fmtHour(h)}</div>
            </div>
          ))}
        </div>
        <DayColumn day={anchor} events={dayEvents} onNewEvent={onNewEvent} onOpenEvent={onOpenEvent} />
      </div>
    </div>
  );
}

function DayColumn({ day, events, onNewEvent, onOpenEvent }) {
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const handleGridClick = (h) => {
    const start = new Date(day); start.setHours(h, 0, 0, 0);
    const end = new Date(start); end.setHours(h + 1);
    onNewEvent({ start: toLocalDatetime(start), end: toLocalDatetime(end) });
  };
  return (
    <div className="relative border-l" style={{ borderColor: "#e2e8f0" }}>
      {hours.map((h) => (
        <div key={h} onClick={() => handleGridClick(h)}
          className="cursor-pointer hover:bg-blue-50/40"
          style={{ height: HOUR_HEIGHT, borderTop: "1px solid #f1f5f9" }} />
      ))}
      {events.map((ev) => (
        <EventTile key={ev.id} event={ev} day={day} onOpen={() => onOpenEvent(ev)} />
      ))}
    </div>
  );
}

function EventTile({ event, day, onOpen }) {
  const startDate = new Date(event.start);
  const endDate = new Date(event.end);
  const dayStart = startOfDay(day);
  const dayEnd = addDays(dayStart, 1);
  const s = startDate < dayStart ? dayStart : startDate;
  const e = endDate > dayEnd ? dayEnd : endDate;
  const topMin = (s - dayStart) / 60000;
  const durMin = Math.max(20, (e - s) / 60000);
  const top = (topMin / 60) * HOUR_HEIGHT;
  const height = (durMin / 60) * HOUR_HEIGHT - 2;
  const isTask = event.source === "task";
  const isDone = isTask && event.status === "done";
  const color = event.color || "#3b82f6";
  return (
    <button data-testid={`cal-event-${event.id}`} onClick={onOpen}
      className="absolute left-1 right-1 rounded-md text-left px-2 py-1 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
      style={{ top, height, backgroundColor: color, color: "#ffffff", zIndex: 2 }}>
      <div className="text-[11px] font-semibold truncate" style={{ color: "#ffffff", textDecoration: isDone ? "line-through" : "none", textDecorationColor: isDone ? "#ef4444" : undefined, textDecorationThickness: isDone ? "2px" : undefined }}>{event.title}</div>
      <div className="text-[10px] opacity-90 truncate" style={{ color: "#ffffff" }}>
        {startDate.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
        {isTask && <span> · Task</span>}
      </div>
    </button>
  );
}

// ---------- MonthView ----------
function MonthView({ anchor, events, onOpenEvent, onDayClick }) {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const gridStart = addDays(first, -first.getDay());
  const days = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  const eventsByDay = useMemo(() => {
    const m = {};
    events.forEach((ev) => {
      try {
        const key = new Date(ev.start).toDateString();
        (m[key] = m[key] || []).push(ev);
      } catch { /* ignore */ }
    });
    return m;
  }, [events]);
  return (
    <div className="p-2">
      <div className="grid grid-cols-7 text-[11px] font-semibold text-center py-2 uppercase" style={{ color: "#64748b" }}>
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => <div key={d}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-px rounded overflow-hidden" style={{ backgroundColor: "#e2e8f0" }}>
        {days.map((d) => {
          const isOther = d.getMonth() !== anchor.getMonth();
          const isToday = isSameDay(d, new Date());
          const rows = eventsByDay[d.toDateString()] || [];
          return (
            <div key={d.toISOString()} className="min-h-[92px] p-1.5" style={{ backgroundColor: isOther ? "#f8fafc" : "#ffffff" }}>
              <button onClick={() => onDayClick(startOfDay(d))} className="h-6 w-6 rounded-full inline-flex items-center justify-center text-[12px] font-medium" style={{ backgroundColor: isToday ? "#2563eb" : "transparent", color: isToday ? "#ffffff" : (isOther ? "#94a3b8" : "#0f172a") }}>{d.getDate()}</button>
              <div className="mt-1 space-y-0.5">
                {rows.slice(0, 3).map((ev) => (
                  <button key={ev.id} onClick={() => onOpenEvent(ev)} className="w-full text-left px-1.5 py-0.5 rounded text-[11px] truncate" style={{ backgroundColor: (ev.color || "#3b82f6") + "22", color: ev.color || "#1d4ed8", textDecoration: ev.source === "task" && ev.status === "done" ? "line-through" : "none", textDecorationColor: "#ef4444" }}>{ev.title}</button>
                ))}
                {rows.length > 3 && <div className="text-[10px] px-1.5" style={{ color: "#64748b" }}>+{rows.length - 3} more</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------- EventModal (create/edit) ----------
function EventModal({ initial, onClose, onSaved, onDeleted, calendars, staff, customers, canManage }) {
  const editing = !!initial?.id;
  const editable = canManage;
  const defStart = initial?.start ? (initial.id ? toLocalDatetime(new Date(initial.start)) : initial.start) : toLocalDatetime(new Date(Date.now() + 3600000));
  const defEnd = initial?.end ? (initial.id ? toLocalDatetime(new Date(initial.end)) : initial.end) : toLocalDatetime(new Date(Date.now() + 3600000 * 2));

  const [title, setTitle] = useState(initial?.title || "");
  const [start, setStart] = useState(defStart);
  const [end, setEnd] = useState(defEnd);
  const [allDay, setAllDay] = useState(!!initial?.allDay);
  const [calendarId, setCalendarId] = useState(initial?.calendarId || calendars[0]?.id || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [location, setLocation] = useState(initial?.location || "");
  const [meetingLink, setMeetingLink] = useState(initial?.meetingLink || "");
  const [attachmentUrl, setAttachmentUrl] = useState(initial?.attachmentUrl || "");
  const [attachmentName, setAttachmentName] = useState(initial?.attachmentName || "");
  const [busy, setBusy] = useState(initial?.busy || "busy");
  const [privacy, setPrivacy] = useState(initial?.privacy || "default");
  const [guests, setGuests] = useState(initial?.guests || []);
  const [notifications, setNotifications] = useState(
    initial?.notifications && initial.notifications.length
      ? initial.notifications.map((n) => ({ minutesBefore: n.minutesBefore }))
      : [{ minutesBefore: 30 }]
  );
  const [saving, setSaving] = useState(false);
  const [showGuestPicker, setShowGuestPicker] = useState(false);
  const [guestQuery, setGuestQuery] = useState("");

  const save = async () => {
    if (!title.trim()) { toast.error("Title required"); return; }
    if (!calendarId) { toast.error("Select a calendar"); return; }
    setSaving(true);
    try {
      const payload = {
        calendarId, title: title.trim(), description, allDay,
        start: new Date(start).toISOString(), end: new Date(end).toISOString(),
        location, meetingLink, attachmentUrl, attachmentName,
        guests, busy, privacy, notifications,
      };
      if (editing) await calendarApi.updateEvent(initial.id, payload);
      else await calendarApi.createEvent(payload);
      toast.success(editing ? "Event updated" : "Event created");
      onSaved && onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save event");
    } finally { setSaving(false); }
  };

  const del = async () => {
    if (!editing) return;
    if (!window.confirm("Delete this event?")) return;
    try {
      await calendarApi.removeEvent(initial.id);
      toast.success("Event deleted");
      onDeleted && onDeleted();
    } catch { toast.error("Failed to delete"); }
  };

  const addGuest = (guest) => {
    setGuests((prev) => prev.some((g) => g.id === guest.id && g.type === guest.type) || prev.some((g) => g.email && g.email === guest.email) ? prev : [...prev, guest]);
    setGuestQuery("");
  };
  const removeGuest = (i) => setGuests((prev) => prev.filter((_, idx) => idx !== i));

  const guestOptions = useMemo(() => {
    const q = guestQuery.trim().toLowerCase();
    if (!q) return [];
    const isEmail = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(q);
    const users = staff
      .filter((u) => (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q))
      .slice(0, 5)
      .map((u) => ({ type: "user", id: u.id, email: u.email, name: u.name || u.email }));
    const custs = customers
      .filter((c) => (c.name || "").toLowerCase().includes(q) || (c.email || "").toLowerCase().includes(q))
      .slice(0, 5)
      .map((c) => ({ type: "customer", id: c.id, email: c.email, name: c.name }));
    const emails = isEmail ? [{ type: "email", email: q, name: q }] : [];
    return [...users, ...custs, ...emails];
  }, [guestQuery, staff, customers]);

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4" style={{ backgroundColor: "rgba(15,23,42,0.35)" }} onClick={onClose}>
      <div data-testid="cal-event-modal" onClick={(e) => e.stopPropagation()}
        className="cal-panel w-full max-w-[560px] rounded-lg shadow-2xl overflow-hidden"
        style={{ maxHeight: "90vh" }}>
        <div className="flex items-center gap-2 px-5 py-3 border-b cal-divider">
          <div className="text-[13px] font-semibold flex-1 cal-label">{editing ? "Edit event" : "New event"}</div>
          {editing && editable && <button onClick={del} className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-red-50" data-testid="cal-event-delete"><Trash2 className="h-4 w-4" color="#dc2626" /></button>}
          <button onClick={onClose} className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-slate-100" data-testid="cal-event-close"><X className="h-4 w-4" color="#475569" /></button>
        </div>
        <div className="px-5 pt-4 pb-2 overflow-y-auto" style={{ maxHeight: "calc(90vh - 120px)" }}>
          <input data-testid="cal-event-title" value={title} onChange={(e) => setTitle(e.target.value)} disabled={!editable}
            placeholder="Add title"
            className="cal-input w-full text-[22px] outline-none border-b py-2 mb-4 focus:border-blue-500"
            style={{ borderColor: "#cbd5e1" }} />

          <FieldRow icon={Clock}>
            <div className="flex items-center gap-2 flex-wrap">
              <input type="datetime-local" data-testid="cal-event-start" value={start} onChange={(e) => setStart(e.target.value)} disabled={!editable}
                className="cal-input rounded border px-2 py-1 text-[13px]" />
              <span className="cal-subtle">—</span>
              <input type="datetime-local" data-testid="cal-event-end" value={end} onChange={(e) => setEnd(e.target.value)} disabled={!editable}
                className="cal-input rounded border px-2 py-1 text-[13px]" />
              <label className="inline-flex items-center gap-1 text-[12px] cal-label">
                <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} disabled={!editable} data-testid="cal-event-allday" /> All day
              </label>
            </div>
          </FieldRow>

          <FieldRow icon={Users}>
            <div className="w-full">
              <div className="flex flex-wrap gap-1.5 mb-1.5" data-testid="cal-event-guests-list">
                {guests.map((g, i) => (
                  <span key={i} className="inline-flex items-center gap-1 text-[12px] rounded-full px-2 py-0.5" style={{ backgroundColor: "#dbeafe", color: "#1d4ed8" }}>
                    <span style={{ color: "#1d4ed8" }}>{g.name || g.email}</span>
                    {editable && <button onClick={() => removeGuest(i)} className="ml-0.5"><X className="h-3 w-3" color="#1d4ed8" /></button>}
                  </span>
                ))}
              </div>
              {editable && (
                <div className="relative">
                  <input value={guestQuery} onChange={(e) => { setGuestQuery(e.target.value); setShowGuestPicker(true); }}
                    onFocus={() => setShowGuestPicker(true)}
                    placeholder="Add guests — staff, customers, or an email"
                    className="cal-input w-full text-[13px] rounded border px-2 py-1.5"
                    data-testid="cal-event-guest-input" />
                  {showGuestPicker && guestOptions.length > 0 && (
                    <div className="absolute z-10 left-0 right-0 mt-1 rounded border shadow-md" style={{ backgroundColor: "#ffffff", borderColor: "#e2e8f0" }}>
                      {guestOptions.map((opt, i) => (
                        <button key={i} onClick={() => addGuest(opt)} className="w-full text-left px-3 py-2 text-[12.5px] hover:bg-slate-50 border-b last:border-b-0" style={{ borderColor: "#f1f5f9", color: "#0f172a", backgroundColor: "#ffffff" }} data-testid={`cal-event-guest-suggestion-${i}`}>
                          <span className="font-medium" style={{ color: "#0f172a" }}>{opt.name || opt.email}</span>
                          <span className="text-[11px] ml-2" style={{ color: "#64748b" }}>· {opt.type}{opt.email ? ` · ${opt.email}` : ""}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </FieldRow>

          <FieldRow icon={Video}>
            <input value={meetingLink} onChange={(e) => setMeetingLink(e.target.value)} disabled={!editable}
              placeholder="Add meeting link (Meet, Zoom, Teams…)"
              className="cal-input w-full text-[13px] rounded border px-2 py-1.5"
              data-testid="cal-event-meeting" />
          </FieldRow>

          <FieldRow icon={MapPin}>
            <input value={location} onChange={(e) => setLocation(e.target.value)} disabled={!editable}
              placeholder="Add location"
              className="cal-input w-full text-[13px] rounded border px-2 py-1.5"
              data-testid="cal-event-location" />
          </FieldRow>

          <FieldRow icon={AlignLeft}>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} disabled={!editable}
              placeholder="Add description or attachment link"
              rows={2}
              className="cal-input w-full text-[13px] rounded border px-2 py-1.5 resize-none"
              data-testid="cal-event-description" />
            <input value={attachmentUrl} onChange={(e) => setAttachmentUrl(e.target.value)} disabled={!editable}
              placeholder="Attachment URL (optional)"
              className="cal-input mt-1.5 w-full text-[12px] rounded border px-2 py-1" />
          </FieldRow>

          <div className="border-t my-3 cal-divider" />

          <FieldRow icon={CalIcon}>
            <select data-testid="cal-event-calendar" value={calendarId} onChange={(e) => setCalendarId(e.target.value)} disabled={!editable}
              className="cal-input text-[13px] rounded border px-2 py-1.5">
              {calendars.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </FieldRow>

          <FieldRow icon={Briefcase}>
            <select value={busy} onChange={(e) => setBusy(e.target.value)} disabled={!editable}
              data-testid="cal-event-busy"
              className="cal-input text-[13px] rounded border px-2 py-1.5">
              <option value="busy">Busy</option>
              <option value="free">Free</option>
            </select>
          </FieldRow>

          <FieldRow icon={Lock}>
            <select value={privacy} onChange={(e) => setPrivacy(e.target.value)} disabled={!editable}
              data-testid="cal-event-privacy"
              className="cal-input text-[13px] rounded border px-2 py-1.5">
              <option value="default">Default visibility</option>
              <option value="public">Public</option>
              <option value="private">Private</option>
            </select>
          </FieldRow>

          <FieldRow icon={Bell}>
            <div className="w-full space-y-1.5" data-testid="cal-event-notifications">
              {notifications.map((n, i) => (
                <NotificationRow key={i} value={n.minutesBefore} disabled={!editable}
                  onChange={(mb) => setNotifications((prev) => prev.map((x, j) => j === i ? { minutesBefore: mb } : x))}
                  onRemove={editable ? () => setNotifications((prev) => prev.filter((_, j) => j !== i)) : null} />
              ))}
              {editable && (
                <button onClick={() => setNotifications((prev) => [...prev, { minutesBefore: 15 }])}
                  data-testid="cal-event-add-notification"
                  className="text-[12.5px] font-medium hover:underline" style={{ color: "#2563eb" }}>+ Add notification</button>
              )}
            </div>
          </FieldRow>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t cal-divider" style={{ backgroundColor: "#dfe3e8" }}>
          <button onClick={onClose} className="px-3 h-9 text-[13px] font-medium rounded cal-label" data-testid="cal-event-cancel">Cancel</button>
          {editable && (
            <button onClick={save} disabled={saving} className="cal-primary-btn px-4 h-9 rounded-full text-[13.5px] font-semibold shadow-sm" data-testid="cal-event-save">
              {saving ? "Saving…" : "Save"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function FieldRow({ icon: Icon, children }) {
  return (
    <div className="flex items-start gap-3 py-2">
      <div className="shrink-0 pt-1.5"><Icon className="h-4 w-4" color="#64748b" /></div>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

function NotificationRow({ value, onChange, onRemove, disabled }) {
  const preset = NOTIFY_PRESETS.find((p) => p.minutes === value);
  const isCustom = !preset;
  const [customVal, setCustomVal] = useState(isCustom ? String(value) : "");
  return (
    <div className="flex items-center gap-2">
      <select value={isCustom ? "custom" : String(value)} disabled={disabled}
        onChange={(e) => {
          if (e.target.value === "custom") {
            const v = Number(customVal) || 5;
            onChange(v);
          } else onChange(Number(e.target.value));
        }}
        className="cal-input text-[13px] rounded border px-2 py-1">
        {NOTIFY_PRESETS.map((p) => <option key={p.minutes} value={String(p.minutes)}>{p.label}</option>)}
        <option value="custom">Custom…</option>
      </select>
      {isCustom && (
        <div className="flex items-center gap-1 text-[12px]">
          <input type="number" min="0" value={customVal} onChange={(e) => { setCustomVal(e.target.value); onChange(Number(e.target.value) || 0); }}
            className="cal-input w-16 rounded border px-1 py-0.5" disabled={disabled} />
          <span className="cal-subtle">min before</span>
        </div>
      )}
      {onRemove && <button onClick={onRemove} className="text-[11px]" style={{ color: "#dc2626" }}><X className="h-3.5 w-3.5" color="#dc2626" /></button>}
    </div>
  );
}

// ---------- CalendarSettingsModal ----------
function CalendarSettingsModal({ cal, staff, canDelete, onClose, onSaved, onDeleted }) {
  const [name, setName] = useState(cal.name || "");
  const [color, setColor] = useState(cal.color || "#3b82f6");
  const [sharedWith, setSharedWith] = useState(cal.sharedWith || []);
  const [saving, setSaving] = useState(false);
  const isCompany = !!cal.isCompany;
  const save = async () => {
    setSaving(true);
    try {
      await calendarApi.updateCalendar(cal.id, { name: name.trim(), color, sharedWith });
      toast.success("Calendar updated");
      onSaved && onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  const del = async () => {
    if (!window.confirm("Delete this calendar and all its events?")) return;
    try { await calendarApi.removeCalendar(cal.id); toast.success("Calendar deleted"); onDeleted(); }
    catch { toast.error("Failed to delete"); }
  };
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4" style={{ backgroundColor: "rgba(15,23,42,0.35)" }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="cal-panel w-full max-w-[440px] rounded-lg shadow-2xl overflow-hidden" data-testid="cal-settings-modal">
        <div className="flex items-center gap-2 px-5 py-3 border-b cal-divider">
          <div className="text-[13px] font-semibold flex-1 cal-label">Calendar settings</div>
          <button onClick={onClose} className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-slate-100"><X className="h-4 w-4" color="#475569" /></button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider mb-1 cal-subtle">Name</div>
            <input value={name} onChange={(e) => setName(e.target.value)} disabled={cal.isPersonal}
              className="cal-input w-full text-[14px] rounded border px-2 py-1.5" data-testid="cal-settings-name" />
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider mb-1 cal-subtle">Color</div>
            <div className="flex items-center gap-2 flex-wrap">
              {["#3b82f6","#8b5cf6","#ec4899","#f97316","#10b981","#f59e0b","#06b6d4","#ef4444","#84cc16","#a855f7","#14b8a6"].map((c) => (
                <button key={c} onClick={() => setColor(c)} className="h-7 w-7 rounded-full" style={{ backgroundColor: c, border: color === c ? "2px solid #0f172a" : "2px solid transparent" }} data-testid={`cal-settings-color-${c.slice(1)}`} />
              ))}
            </div>
          </div>
          {!isCompany && (
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider mb-1 cal-subtle">Share with staff</div>
              <div className="max-h-40 overflow-y-auto rounded border" style={{ borderColor: "#cbd5e1", backgroundColor: "#ffffff" }}>
                {staff.filter((u) => u.id !== cal.ownerUserId).map((u) => (
                  <label key={u.id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer text-[13px]" style={{ color: "#0f172a", backgroundColor: "#ffffff" }}>
                    <input type="checkbox" checked={sharedWith.includes(u.id)} onChange={(e) => setSharedWith((prev) => e.target.checked ? [...prev, u.id] : prev.filter((x) => x !== u.id))} data-testid={`cal-share-${u.id}`} />
                    <span style={{ color: "#0f172a" }}>{u.name || u.email}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 px-5 py-3 border-t cal-divider" style={{ backgroundColor: "#dfe3e8" }}>
          {canDelete && <button onClick={del} className="text-[13px] font-medium" style={{ color: "#dc2626" }}>Delete</button>}
          <div className="flex-1" />
          <button onClick={onClose} className="px-3 h-9 text-[13px] font-medium rounded cal-label">Cancel</button>
          <button onClick={save} disabled={saving} className="cal-primary-btn px-4 h-9 rounded-full text-[13.5px] font-semibold shadow-sm" data-testid="cal-settings-save">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- NewTaskModal ----------
function NewTaskModal({ staff, customers, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [assigneeIds, setAssigneeIds] = useState([]);
  const [dueAt, setDueAt] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const toggle = (id) => setAssigneeIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return setError("Task title is required");
    setBusy(true); setError("");
    try {
      await tasksApi.create({ title: title.trim(), notes, assigneeIds, dueAt: dueAt || null, customerId: customerId || null });
      toast.success("Task created");
      onCreated && onCreated();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not create task");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4" style={{ backgroundColor: "rgba(15,23,42,0.35)" }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="cal-panel w-full max-w-[440px] rounded-lg shadow-2xl overflow-hidden" data-testid="cal-new-task-modal">
        <div className="flex items-center gap-2 px-5 py-3 border-b cal-divider">
          <div className="text-[14px] font-semibold flex-1 cal-label">New task</div>
          <button onClick={onClose} className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-slate-100" data-testid="cal-new-task-close"><X className="h-4 w-4" color="#475569" /></button>
        </div>
        <form onSubmit={submit} className="px-5 py-4 space-y-3">
          <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Task title"
            className="cal-input w-full h-10 px-3 rounded-lg border text-[13.5px]" data-testid="cal-new-task-title" />
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Notes (optional)"
            className="cal-input w-full px-3 py-2 rounded-lg border text-[13.5px] resize-y" data-testid="cal-new-task-notes" />
          <div>
            <div className="text-[12px] font-semibold cal-label mb-1.5">Assign to (one or more)</div>
            <div className="max-h-36 overflow-y-auto rounded-lg border divide-y" style={{ borderColor: "#cbd5e1", backgroundColor: "#ffffff" }} data-testid="cal-new-task-assignees">
              {staff.length === 0 && <div className="px-3 py-2 text-[12px] cal-subtle">No staff available</div>}
              {staff.map((u) => (
                <label key={u.id} className="flex items-center gap-2 px-3 py-1.5 text-[12.5px] cursor-pointer hover:bg-slate-50" style={{ backgroundColor: "#ffffff", color: "#0f172a" }}>
                  <input type="checkbox" checked={assigneeIds.includes(u.id)} onChange={() => toggle(u.id)} data-testid={`cal-new-task-assign-${u.id}`} className="h-3.5 w-3.5" />
                  <span style={{ color: "#0f172a" }}>{u.name || u.email}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[12px] font-semibold cal-label mb-1.5">Due date</div>
            <input type="date" value={dueAt} onChange={(e) => setDueAt(e.target.value)}
              className="cal-input w-full h-10 px-3 rounded-lg border text-[13.5px]" data-testid="cal-new-task-due" />
          </div>
          <select value={customerId} onChange={(e) => setCustomerId(e.target.value)}
            className="cal-input w-full h-10 px-3 rounded-lg border text-[13.5px]" data-testid="cal-new-task-customer">
            <option value="">Link a customer (optional)</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {error && (
            <div className="px-3 py-2 rounded-md text-[12.5px] flex items-center gap-1.5" style={{ backgroundColor: "#fee2e2", border: "1px solid #fecaca", color: "#b91c1c" }}>
              <AlertTriangle className="h-3.5 w-3.5" color="#b91c1c" /> <span style={{ color: "#b91c1c" }}>{error}</span>
            </div>
          )}
          <div className="flex items-center justify-end gap-2 pt-1">
            <button type="button" onClick={onClose} className="px-3 h-9 text-[13px] font-medium rounded cal-label" data-testid="cal-new-task-cancel">Cancel</button>
            <button type="submit" disabled={busy} className="cal-primary-btn px-4 h-9 rounded text-[13.5px] font-semibold" data-testid="cal-new-task-submit">
              {busy ? "Saving…" : "Create task"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
